import { Controller, FieldValues, Control, FieldArrayWithId, UseFormWatch } from "react-hook-form";
import { Grid, Typography, Button, TextField, Divider, Stack, IconButton } from "@mui/material";
import { useEffect } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import { extractBirthDateFromPESEL, validatePESEL } from "../util/util";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { green } from "@mui/material/colors";
import AppDateInputForPesel from "./AppDateInputForPesel";
import { OrderFormValues } from "../models/orderFormValues";
import { toast } from "react-toastify";

interface AppStringConnectorProps {
    control: Control<FieldValues>;
    remove: (index?: number | number[] | undefined) => void;
    fields: Partial<FieldArrayWithId<FieldValues, "id">>[];
    append: (value: FieldValues | FieldValues[]) => void;
    dataToLoad?: { firstName: string, lastName: string, pesel: string, birthPlace: string, birthDate: string }[];
    customBackgroundColor?: string;
    setValue: (name: string, value: any) => void;
    watch: UseFormWatch<OrderFormValues>;
    label: string;
    handleSetTotalItemRows: (count: number) => void;
}

export const AppOrderBenefitientsStringsConnector: React.FC<AppStringConnectorProps> = ({ control, remove, fields, append, dataToLoad, customBackgroundColor, watch, label, handleSetTotalItemRows, setValue }) => {
    useEffect(() => {
        handleSetTotalItemRows(fields.length);
    }, [fields.length, handleSetTotalItemRows])

    useEffect(() => {
        // Only add default empty row if there are no fields AND no data to load
        if (fields.length === 0 && !dataToLoad) {
            append({ firstName: "", lastName: "", pesel: "-", birthPlace: "", singleTaskGroupNumber: "", birthDate: "", singleTaskGroupFrequency: "" });
        } else if (dataToLoad && fields.length === 0) {
            // If we have data to load and no fields, append the data
            dataToLoad.forEach(data => {
                console.log(data);
                append(data);
            });
        }
    }, [append, dataToLoad, fields.length]);

    return (
        <>
            <Grid item xs={12}>
                <Typography variant="h5">{label}</Typography>
            </Grid>

            {fields.map((field, index) => (

                <Grid spacing={1} container key={field.id} sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ ml: 1 }}>{index + 1}.</Typography>
                    <Grid item xs={12} md={2}>
                        <Controller
                            control={control}
                            name={`rows.${index}.firstName`}
                            render={({ field }) => (
                                <TextField
                                    required
                                    fullWidth
                                    variant="outlined"
                                    {...field}
                                    onChange={(e) => {
                                        e.target.value = e.target.value.replace(/[~;]/g, '');
                                        field.onChange(e);
                                    }}
                                    placeholder="Imię"
                                    sx={{ backgroundColor: customBackgroundColor }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Controller
                            control={control}
                            name={`rows.${index}.lastName`}
                            render={({ field }) => (
                                <TextField
                                    required
                                    fullWidth
                                    variant="outlined"
                                    {...field}
                                    onChange={(e) => {
                                        e.target.value = e.target.value.replace(/[~;]/g, '');
                                        field.onChange(e);
                                    }}
                                    placeholder="Nazwisko"
                                    sx={{ backgroundColor: customBackgroundColor }}
                                />
                            )}
                        />
                    </Grid>
                    {/* klient powinien móc wpisać co chce lub zostawić puste/- jednak sprawdzanie 
                    numeru PESEL powinno wyświetlić komunikat, że pesel nie jest prawidłowy, 
                    ale nie broni to zapisaniu i wygenerowaniu certyfikatów */}
                    <Grid item xs={12} sm={6} md={2}>
                        <Controller
                            control={control}
                            name={`rows.${index}.pesel`}
                            render={({ field }) => (
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    {...field}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        field.onChange(value);
                                    }}
                                    onBlur={(e) => {
                                        const value = e.target.value;
                                        if (!value || value.trim() === '') {
                                            field.onChange('-');
                                        }
                                        field.onBlur();
                                    }}
                                    placeholder="Pesel"
                                    sx={{ backgroundColor: customBackgroundColor }}
                                    InputProps={{
                                        endAdornment: field.value &&
                                            field.value !== '-' &&
                                            validatePESEL(field.value) && (
                                                <CheckCircleIcon sx={{ color: green[500] }} />
                                            )
                                    }}
                                    error={field.value &&
                                        field.value !== '-' &&
                                        field.value.trim() !== '' &&
                                        !validatePESEL(field.value)}
                                    helperText={field.value &&
                                        field.value !== '-' &&
                                        field.value.trim() !== '' &&
                                        !validatePESEL(field.value) ?
                                        "Nieprawidłowy PESEL, ale zostanie zapisany w powyższej formie" : ""}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Controller
                            control={control}
                            name={`rows.${index}.birthPlace`}
                            render={({ field }) => (
                                <TextField
                                    required
                                    fullWidth
                                    variant="outlined"
                                    {...field}
                                    onChange={(e) => {
                                        e.target.value = e.target.value.replace(/[~;]/g, '');
                                        field.onChange(e);
                                    }}
                                    placeholder="Miejsce urodzenia"
                                    sx={{ backgroundColor: customBackgroundColor }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <AppDateInputForPesel
                            name={`rows.${index}.birthDate`}
                            control={control}
                            label="Data urodzenia"
                            peselValue={watch ? watch(`rows.${index}.pesel`) : undefined}
                            onExtractFromPesel={() => {
                                if (!watch || !setValue) return;
                                const peselValue = watch(`rows.${index}.pesel`);
                                if (peselValue && validatePESEL(peselValue)) {
                                    const birthDate = extractBirthDateFromPESEL(peselValue);
                                    if (birthDate) {
                                        setValue(`rows.${index}.birthDate`, birthDate);
                                    } else {
                                        toast.error('Nie udało się wyodrębnić daty urodzenia z PESEL-u');
                                    }
                                } else {
                                    toast.error('Podaj prawidłowy numer PESEL');
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm container>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" flexWrap="nowrap">
                            <Controller
                                control={control}
                                name={`rows.${index}.certId`}
                                render={({ field }) => (
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        {...field}
                                        disabled={true}
                                        placeholder="Certyfikat"
                                        sx={{ backgroundColor: customBackgroundColor }}
                                    />
                                )}
                            />
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                    remove(index);
                                }}
                                disabled={fields.length === 1}>
                                <DeleteIcon />
                            </IconButton>
                        </Stack>
                    </Grid>
                    {index + 1 !== fields.length && (
                        <Grid item xs={12}>
                            <Divider variant="middle" sx={{ my: 3 }} />
                        </Grid>
                    )}
                </Grid >
            ))}
            <Button
                variant="outlined"
                onClick={() => { append({ firstName: "", lastName: "", pesel: "", birthPlace: "", birthDate: "" }); }}
                sx={{ mt: 3 }}>
                Dodaj Uczestnika
            </Button>
        </>
    );
};